import { ReactElement } from 'react';

import { Notification } from '@dreryk/egabinet-user-inbox-frontend-api';

import { LinkVariant, NotificationIcon, usePush } from '@dreryk/react-components';

import { formatBadgeCount } from '../ConfidentialityNotificationsBadge.utils';
import { StyledBadgeCount } from '../Badge.styled';

import { NotificationItem } from './NotificationItem/NotificationItem';
import { getNotificationPath } from './NotificationItem/NotificationItem.utils';
import {
   useMarkNotificationsAsRead,
   useNotifications,
   useUnreadNotificationsCount,
} from './ConfidentialityNotificationsIcon.hooks';
import {
   StyledConfidentialityNotificationsDropdown,
   StyledConfidentialityNotificationsTrigger,
   StyledNotificationDropdownItem,
} from './ConfidentialityNotificationsIcon.styled';

export const ConfidentialityNotificationsIcon = (): ReactElement | null => {
   const push = usePush();
   const { data: notifications, setData: setNotifications } = useNotifications();
   const { data: unreadCount } = useUnreadNotificationsCount();
   const { execute: markAsRead } = useMarkNotificationsAsRead();

   if (!notifications || notifications.length === 0) {
      return null;
   }

   const displayedCount = formatBadgeCount(unreadCount ?? 0);

   const sortedNotifications = [...notifications].sort(
      (first, second) => new Date(second.createdAt).getTime() - new Date(first.createdAt).getTime()
   );

   const handleNotificationClick = (notification: Notification, hide: VoidFunction): void => {
      if (!notification.read) {
         markAsRead([notification.id]);
         setNotifications((current) =>
            (current ?? []).map((item) => (item.id === notification.id ? { ...item, read: true } : item))
         );
      }

      const path = getNotificationPath(notification);

      if (path) {
         push(path);
      }

      hide();
   };

   return (
      <StyledConfidentialityNotificationsDropdown
         testId={'confidentialityNotifications'}
         variant={LinkVariant.SUCCESS}
         compact
         triggerIconSuffixHidden
         triggerLabel={
            <StyledConfidentialityNotificationsTrigger>
               <NotificationIcon />
               {unreadCount ? (
                  <StyledBadgeCount data-testid={'confidentialityNotificationsCount'}>
                     {displayedCount}
                  </StyledBadgeCount>
               ) : null}
            </StyledConfidentialityNotificationsTrigger>
         }
      >
         {sortedNotifications.map((notification) => (
            <StyledNotificationDropdownItem
               key={notification.id}
               testId={`notification-${notification.id}`}
               onClick={(hide) => handleNotificationClick(notification, hide)}
            >
               <NotificationItem notification={notification} />
            </StyledNotificationDropdownItem>
         ))}
      </StyledConfidentialityNotificationsDropdown>
   );
};
